import {
  type AudienceSegmentEntry,
  type AudienceSegmentSet,
  AudienceSegmentSetType,
  type CumulativeBundleSegment,
  type CumulativeDomainSegment,
  type CumulativeSegment,
  type MatchedSegment,
  type NormalizedDefinition,
} from '@openx/types/gqlTypes.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import type { TFunction } from 'i18next';
import type { DealPromptTileHeaderProps } from '../parts/tiles/DealPromptTileHeader.tsx';
import type { DealPromptFilterGoal, DealPromptFilters } from './types.ts';

export const segments = {
  BEST_SCALE: 'Scale Optimized',
  // TODO: Remove deprecated when EXCH-14669 is completed
  BEST_SCALE_DEPRECATED: 'Direct Publishers',
  BEST_RELEVANCE: 'Relevance Focused',
  BEST_PERFORMANCE: 'Performance Optimized',
  HIGHEST_SCALE: 'Highest Scale',
  MOST_RELEVANT: 'Most Relevant',
  BEST_PERFORMANCE_AUDIENCE: 'Best Performance',
};

type TileData = {
  segment: ConstValues<typeof segments>;
  item: CumulativeSegment | AudienceSegmentSetWithSegments | null;
} & Omit<DealPromptTileHeaderProps, 'contextType' | 'subtitle'>;

type TilesDataReturn = [TileData, TileData, TileData];

export type AudienceSegmentSetWithSegments = AudienceSegmentSet & {
  matchedSegments: MatchedSegment[];
  segments: AudienceSegmentEntry[];
  normalizedDefinition: NormalizedDefinition | null;
};

export type DealPromptTileItem = CumulativeDomainSegment | CumulativeBundleSegment | AudienceSegmentSetWithSegments;

export function isAudienceSegmentSetWithSegmentsTile(item: DealPromptTileItem): item is AudienceSegmentSetWithSegments {
  return 'matchedSegments' in item || 'segmentIds' in item;
}

export function isCumulativeDealPromptTileItem(
  item: DealPromptTileItem
): item is CumulativeDomainSegment | CumulativeBundleSegment {
  return !isAudienceSegmentSetWithSegmentsTile(item);
}

export const toAudienceSegmentSet = (tile: AudienceSegmentSetWithSegments): AudienceSegmentSet => {
  const { segments: _segments, normalizedDefinition: _normalizedDefinition, ...segmentSet } = tile;
  return segmentSet;
};

const getSegmentItem = (
  segment: ConstValues<typeof segments>,
  cumulativeData: CumulativeSegment[]
): CumulativeSegment | null => cumulativeData.find(i => i.segment === segment && i.noOfResults > 0) ?? null;

export const getSegmentIdsFromSet = (segmentSet?: AudienceSegmentSet): string[] => {
  if (!segmentSet) return [];
  if (segmentSet.matchedSegments?.length) {
    const matchedSegmentIds = segmentSet.matchedSegments
      .map(segment => segment.id)
      .filter((segmentId): segmentId is string => Boolean(segmentId));
    if (matchedSegmentIds.length) {
      return matchedSegmentIds;
    }
  }
  // Backward compatibility while backend transitions away from segmentIds.
  return segmentSet.segmentIds ?? [];
};

const getSegmentsForSet = (
  segmentSet: AudienceSegmentSet | undefined,
  audienceSegments: AudienceSegmentEntry[] | null
): AudienceSegmentEntry[] => {
  if (!segmentSet || !audienceSegments?.length) {
    return [];
  }

  const segmentIds = getSegmentIdsFromSet(segmentSet);
  const segmentsFromPrimaryIds = audienceSegments.filter(segment => segmentIds.includes(segment.id));

  if (segmentsFromPrimaryIds.length > 0 || !segmentSet.segmentIds?.length) {
    return segmentsFromPrimaryIds;
  }

  return audienceSegments.filter(segment => segmentSet.segmentIds.includes(segment.id));
};

const getBestPerformanceItemTitleSuggestion = (
  goalFilterCriteria: DealPromptFilterGoal | undefined,
  t: TFunction
): string => {
  if (goalFilterCriteria === 'impressions') {
    return t('Impressions');
  }

  if (goalFilterCriteria === 'ctr') {
    return t('CTR');
  }

  return t('based on Spend');
};

export const getTilesData = (
  cumulativeData: CumulativeSegment[],
  t: TFunction,
  lastSubmittedFilterCriteria: AllTableCriteria<DealPromptFilters>
): TilesDataReturn => {
  let bestScaleItem = getSegmentItem(segments.BEST_SCALE, cumulativeData);

  // TODO: remove fallback when EXCH-14669 is done
  // We need to fallback to legacy segment until BE is completed
  if (!bestScaleItem) {
    bestScaleItem = getSegmentItem(segments.BEST_SCALE_DEPRECATED, cumulativeData);
  }

  const bestRelevanceItem = getSegmentItem(segments.BEST_RELEVANCE, cumulativeData);
  const bestPerformanceItem = getSegmentItem(segments.BEST_PERFORMANCE, cumulativeData);
  const bestPerformanceItemTitleSuggestion = getBestPerformanceItemTitleSuggestion(
    lastSubmittedFilterCriteria.filters?.goal,
    t
  );

  const bestScaleTile: TileData = {
    title: t('Best Scale'),
    segment: segments.BEST_SCALE,
    item: bestScaleItem,
  };

  const bestRelevanceTile: TileData = {
    title: t('Best Relevance'),
    segment: segments.BEST_RELEVANCE,
    item: bestRelevanceItem,
  };

  const bestPerformanceTile: TileData = {
    title: t('Best Performance'),
    titleSuggestion: bestPerformanceItemTitleSuggestion,
    segment: segments.BEST_PERFORMANCE,
    item: bestPerformanceItem,
  };

  return [bestScaleTile, bestRelevanceTile, bestPerformanceTile];
};

export const getAudienceTilesData = (
  audienceSegmentsSets: AudienceSegmentSet[],
  audienceSegments: AudienceSegmentEntry[] | null,
  t: TFunction
): TilesDataReturn => {
  const highestScaleItem: AudienceSegmentSet = audienceSegmentsSets.find(
    segment => segment.type === AudienceSegmentSetType.HighestScale
  )!;
  const mostRelevantItem: AudienceSegmentSet = audienceSegmentsSets.find(
    segment => segment.type === AudienceSegmentSetType.MostRelevant
  )!;
  const bestPerformanceItem: AudienceSegmentSet = audienceSegmentsSets.find(
    segment => segment.type === AudienceSegmentSetType.BestPerformance
  )!;

  const highestScaleTile: TileData = {
    title: t('Highest Scale'),
    titleSuggestion: t('based on Unique Users'),
    segment: segments.HIGHEST_SCALE,
    item: {
      ...highestScaleItem,
      segments: getSegmentsForSet(highestScaleItem, audienceSegments),
      normalizedDefinition: highestScaleItem?.normalized_definition ?? null,
    },
  };

  const mostRelevantTile: TileData = {
    title: t('Most Relevant'),
    segment: segments.MOST_RELEVANT,
    item: {
      ...mostRelevantItem,
      segments: getSegmentsForSet(mostRelevantItem, audienceSegments),
      normalizedDefinition: mostRelevantItem?.normalized_definition ?? null,
    },
  };

  const bestPerformanceTile: TileData = {
    title: t('Best Performance'),
    titleSuggestion: t('based on Impressions'),
    segment: segments.BEST_PERFORMANCE_AUDIENCE,
    item: {
      ...bestPerformanceItem,
      segments: getSegmentsForSet(bestPerformanceItem, audienceSegments),
      normalizedDefinition: bestPerformanceItem?.normalized_definition ?? null,
    },
  };

  return [highestScaleTile, mostRelevantTile, bestPerformanceTile];
};
